/**
 * The next action — one card, always (docs/19 A3, part 2).
 *
 * **The card is the server's.** `next_action` arrives worked out from the case
 * record: the situation, the subject, what is missing, and every offer with
 * everything its control needs. This file draws each offer as a control and
 * posts the command it names. It computes nothing about what is possible — a
 * screen that did would be a second state machine, and the two would disagree
 * the day a rule changed (web/CLAUDE.md).
 *
 * **Every decision is a choice.** Attach the signed paper and click the answer;
 * tick the allegations to charge; pick the penalty from Rule 11's list; mark
 * each charge admitted or not. Never a free-text box where a choice will do —
 * the one place a line of text survives is naming a charge the reading of the
 * papers did not find, and it sits behind its own button.
 *
 * **A blocked offer stays on the card**, greyed, with the rule and the reason
 * (rule 3). **Nothing is pre-selected**: not an answer, not a penalty.
 */

import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import type {
  CaseDetail,
  ChoiceView,
  NextActionView,
  OfferView,
  RegisterPersonView,
} from '../../api/client'
import { ClockPill, ErrorEnvelope } from '../../components/bits'
import { readPeriod } from '../../lib/period'
import {
  answersBlocked,
  decisionBlocked,
  draftNow,
  extensionReason,
  headShowsClock,
  situationWords,
  uploadPaper,
  useRefreshCase,
} from './acting'

export function NextActionCard({ data, readOnly }: { data: CaseDetail; readOnly: boolean }) {
  const card = data.next_action
  if (!card) return null
  return (
    <section
      className={`next-action ${card.situation}${card.held ? ' held' : ''}`}
      data-testid="next-action"
      data-situation={card.situation}
      data-step={card.step ?? ''}
    >
      <div className="na-head">
        <span className="na-situation">{situationWords(card.situation)}</span>
        {card.stage_title ? <span className="na-stage">{card.stage_title}</span> : null}
        {/* **The deadline is said once on this page.** A running period is in
            the head, directly above, with its authority, its citation and its
            due date. The card shows its period only where the head is silent
            about it — one that cannot start yet, or one already satisfied. */}
        {card.clock && !headShowsClock(data.clocks, card.clock.key) ? (
          <CardClock card={card} />
        ) : null}
      </div>
      <h2 className="na-subject" data-testid="next-action-subject">
        {card.subject}
      </h2>
      {card.detail ? <p className="na-detail">{card.detail}</p> : null}
      {card.waiting_on && card.situation === 'wait' ? (
        <p className="na-waiting small muted">Waiting on {card.waiting_on}.</p>
      ) : null}
      {/* The subject already names one missing paper; the list earns its
          place only where there are several. */}
      {(card.missing ?? []).length > 1 && card.situation === 'needs' ? (
        <ul className="na-missing" aria-label="What is missing">
          {(card.missing ?? []).map((item) => (
            <li key={`${item.what}-${item.key}`}>{item.words}</li>
          ))}
        </ul>
      ) : null}
      {(card.offers ?? []).length ? (
        readOnly ? (
          <ul className="na-readonly small muted" data-testid="next-action-readonly">
            {(card.offers ?? []).map((offer, index) => (
              <li key={index}>{offer.label}</li>
            ))}
            <li>Recorded by the Disciplinary Section.</li>
          </ul>
        ) : (
          <div className="na-offers">
            {(card.offers ?? []).map((offer, index) => (
              <OfferControl key={`${card.step}-${index}`} data={data} offer={offer} />
            ))}
          </div>
        )
      ) : null}
    </section>
  )
}

/** The period the card is about, in the one wording (`readPeriod`), and
 *  whether it is the law's or the office's — an officer who thinks a
 *  self-imposed deadline is law refuses an extension he may grant. */
function CardClock({ card }: { card: NextActionView }) {
  const clock = card.clock
  if (!clock) return null
  return (
    <span className="na-clock" title={readPeriod(clock).sentence}>
      <ClockPill from={clock} />
      <span className="small muted">
        {clock.label}
        {clock.authority ? ` · ${clock.authority}` : ''}
      </span>
    </span>
  )
}

/* ================================================================ offers == */

function OfferControl({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  if (offer.blocked) return <BlockedOffer offer={offer} />
  switch (offer.act) {
    case 'draft':
    case 'review':
      return <DocumentLink data={data} offer={offer} />
    case 'upload':
      return <UploadOffer data={data} offer={offer} />
    case 'confirm-kind':
      return <ConfirmKindOffer data={data} offer={offer} />
    case 'issue':
      return <IssueOffer data={data} offer={offer} />
    case 'decide':
      return <DecideOffer data={data} offer={offer} />
    case 'serve':
      return <ServeOffer data={data} offer={offer} />
    case 'reply':
      return <ReplyOffer data={data} offer={offer} />
    case 'extend':
      return <ExtendOffer data={data} offer={offer} />
    case 'answers':
      return <AnswersOffer data={data} offer={offer} />
    case 'no-reply':
      return <NoReplyOffer data={data} offer={offer} />
    case 'fact':
      return <FactOffer data={data} offer={offer} />
    case 'nominate':
      return <NominateOffer data={data} offer={offer} />
    case 'appoint':
      return <AppointOffer data={data} offer={offer} />
    case 'remind':
      return <BlockedOffer offer={{ ...offer, blocked: true }} />
  }
}

/** Greyed, on the card, with its reason and its rule — never hidden. */
function BlockedOffer({ offer }: { offer: OfferView }) {
  return (
    <div className="blocked na-blocked" data-testid={`offer-blocked-${offer.act}`}>
      <button type="button" disabled>
        {offer.label}
      </button>
      <div>
        <div className="why">{offer.reason ?? 'Not open on this case.'}</div>
        {offer.rule ? <div className="small faint mono">{offer.rule}</div> : null}
      </div>
    </div>
  )
}

function DocumentLink({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  if (!offer.document) return null
  const className = offer.primary ? 'btn primary' : 'btn'
  return (
    <div className="na-offer">
      {/* *Draft it* starts the draft: the drafting screen reads this state,
          takes it off the history entry, and starts only where no version
          stands. *Review* carries nothing and opens the document as before. */}
      <Link
        className={className}
        to={`/cases/${data.id}/documents/${offer.document}`}
        state={offer.act === 'draft' ? draftNow(offer.anyway, offer.purpose) : undefined}
        data-testid={`offer-${offer.act}-${offer.document}`}
      >
        {offer.label}
      </Link>
      {offer.document_title && !offer.primary && !offer.anyway ? (
        <span className="small muted"> {offer.document_title}</span>
      ) : null}
    </div>
  )
}

/** A file picker that holds one paper until the act it belongs to is posted. */
function PaperField({
  label,
  file,
  onFile,
  testid,
}: {
  label: string
  file: File | null
  onFile: (file: File | null) => void
  testid?: string
}) {
  return (
    <label className="na-paper">
      {label}
      <input
        type="file"
        aria-label={label}
        data-testid={testid}
        onChange={(event) => onFile(event.target.files?.[0] ?? null)}
      />
      {file ? <span className="small mono muted">{file.name}</span> : null}
    </label>
  )
}

function DateField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
}) {
  return (
    <label className="na-date">
      {label}
      <input
        type="date"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && !value ? <span className="small muted">{hint}</span> : null}
    </label>
  )
}

/**
 * **A date read off someone else's signed paper starts blank.**
 *
 * The service date on the acknowledgement, the day his reply arrived, the day
 * the Inquiry Officer signed for his order, the day his appeal was filed: each
 * starts or closes a period, and a started period's start is never moved
 * afterwards. On this card the date sits
 * one click from attaching the paper that states it, so a date filled in with
 * today would be accepted without anybody reading the paper — the app's guess
 * recorded as the paper's fact (rule 1). The issue date is blank for the same
 * reason. The older forms' ruling that a date defaults to today
 * (components/acts.tsx) stands only where the date neither starts nor closes a
 * period: a note, a decision, the day his answers were read, an extension's
 * grant (compliance reviews, 26 Sep 2026).
 */
const READ_IT_OFF_THE_PAPER = 'As written on the paper — not filled in for you.'

function KindWarning({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p className="small warn-text" data-testid="kind-not-recorded">
      The paper is on the file; what kind it is was not recorded — {message}
    </p>
  )
}

function UploadOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const [file, setFile] = useState<File | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose the paper first.')
      return uploadPaper(data.id, file, offer.paper, offer.records_document)
    },
    onSuccess: (done) => {
      setWarning(done.kindNotRecorded)
      setFile(null)
      refresh()
    },
  })
  return (
    <div className={`na-offer na-form${offer.primary ? ' primary' : ''}`}>
      <PaperField
        label={offer.paper_label ?? 'The paper'}
        file={file}
        onFile={setFile}
        testid={`offer-upload-${offer.paper ?? 'paper'}`}
      />
      <button
        type="button"
        className={offer.primary ? 'primary' : undefined}
        disabled={!file || upload.isPending}
        onClick={() => upload.mutate()}
      >
        {upload.isPending ? 'Uploading…' : offer.label}
      </button>
      <KindWarning message={warning} />
      <ErrorEnvelope error={upload.error} />
    </div>
  )
}

function ConfirmKindOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const confirm = useMutation({
    mutationFn: async () => {
      if (!offer.file_id || !offer.paper) return
      await api.setPaperKind(data.id, offer.file_id, { preflight: false, kind: offer.paper })
      if (offer.records_document) {
        // The guessed paper is an external document on the rail; naming its
        // type records it through the label's own path, like an upload does.
        await api.labelSourceFile(data.id, offer.file_id, {
          preflight: false,
          document_type: offer.records_document,
        })
      }
    },
    onSuccess: refresh,
  })
  return (
    <div className="na-offer">
      <button
        type="button"
        className={offer.primary ? 'primary' : undefined}
        disabled={confirm.isPending}
        onClick={() => confirm.mutate()}
        data-testid={`offer-confirm-${offer.file_id}`}
      >
        {confirm.isPending ? 'Recording…' : offer.label}
      </button>
      <ErrorEnvelope error={confirm.error} />
    </div>
  )
}

function IssueOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  // Blank, like the dates read off a paper: the issue date is the one printed
  // on the document, it can start a period (the Board's report runs from the
  // order appointing it) and it becomes the appointment order's date — and a
  // recorded issue date is not moved afterwards (compliance review, 26 Sep).
  const [on, setOn] = useState('')
  const issue = useMutation({
    mutationFn: () =>
      api.markIssued(data.id, {
        preflight: false,
        document_type: offer.document ?? '',
        occurrence: offer.occurrence ?? 1,
        issued_on: on,
      }),
    onSuccess: refresh,
  })
  return (
    <div className="na-offer na-form">
      <DateField
        label="On"
        value={on}
        onChange={setOn}
        hint="The date the document carries — not filled in for you. The draft's date line is set to it."
      />
      <button
        type="button"
        className={offer.primary ? 'primary' : undefined}
        disabled={!on || !offer.document || issue.isPending}
        onClick={() => issue.mutate()}
        data-testid={`offer-issue-${offer.document}`}
      >
        {issue.isPending ? 'Recording…' : offer.label}
      </button>
      <ErrorEnvelope error={issue.error} />
    </div>
  )
}

/* ---------------------------------------------------------------- decide -- */

export function DecideOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  // One radio group per form: the card and the case file's "Record it again"
  // can both offer the same decision on one page, and a shared `name` would
  // let a click in one silently clear the other.
  const group = useId()
  const [choiceKey, setChoiceKey] = useState<string | null>(null)
  // Where the card completes a decision already on the record — the penalty
  // proposed with a route recorded without one — the date is that order's,
  // which the record holds; otherwise the older forms' default of today.
  const [on, setOn] = useState(offer.on ?? data.as_of)
  const [file, setFile] = useState<File | null>(null)
  const [ticked, setTicked] = useState<string[]>([])
  const [named, setNamed] = useState<string[]>([])
  const [penalty, setPenalty] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const choice: ChoiceView | null =
    (offer.choices ?? []).find((c) => c.key === choiceKey) ?? null

  const charges = [
    ...(data.allegations ?? [])
      .filter((a) => ticked.includes(a.id))
      .map((a) => ({
        heading: a.statement,
        allegation_id: a.id,
        allegation: a.quote ?? a.statement,
      })),
    ...named.filter((line) => line.trim()).map((line) => ({ heading: line.trim() })),
  ]
  const paperNeeded = choice?.paper ?? null
  const blocked = decisionBlocked({
    choice,
    charges: charges.length,
    penalty,
    paper: Boolean(file),
  })

  const record = useMutation({
    mutationFn: async () => {
      if (!choice || !offer.decision) return
      let sourceFileId: string | null = null
      if (file) {
        const kind = paperNeeded ?? offer.paper ?? 'signed-noting'
        const done = await uploadPaper(data.id, file, kind)
        sourceFileId = done.fileId
        setWarning(done.kindNotRecorded)
      }
      // One call. The facts a guard reads behind the answer — the
      // disagreement, the penalty kind `CR-16-1A` reads — are recorded by the
      // server in the decision's own transaction, never by a second call from
      // here that could fail after the first had committed.
      await api.recordDecision(data.id, {
        preflight: false,
        decision: offer.decision,
        outcome: choice.key,
        decided_on: on,
        note: null,
        source_file_id: sourceFileId,
        charges: choice.needs_charges ? charges : null,
        proposed_penalty: choice.needs_penalty ? penalty : null,
        people: null,
      })
    },
    onSuccess: () => {
      setFile(null)
      refresh()
    },
  })

  return (
    <div className="na-offer na-decide" data-testid={`offer-decide-${offer.decision}`}>
      <fieldset className="na-choices">
        <legend>{offer.label}</legend>
        {(offer.choices ?? []).map((option) => (
          <label key={option.key} className={`na-choice${option.query ? ' query' : ''}`}>
            <input
              type="radio"
              name={`${group}-choice`}
              value={option.key}
              checked={choiceKey === option.key}
              onChange={() => setChoiceKey(option.key)}
              data-testid={`choice-${option.key}`}
            />
            <span>{option.label}</span>
            {option.ends ? <span className="small muted"> — ends the case</span> : null}
          </label>
        ))}
      </fieldset>

      {choice?.needs_charges ? (
        <ChargesPicker
          data={data}
          ticked={ticked}
          onTick={(id, on) =>
            setTicked((was) => (on ? [...was, id] : was.filter((item) => item !== id)))
          }
          named={named}
          onNamed={setNamed}
        />
      ) : null}

      {choice?.needs_penalty ? (
        <fieldset className="na-penalties" data-testid="penalties">
          <legend>The penalty the authority proposed — Rule 11</legend>
          {(offer.penalties ?? []).map((option) => (
            <label key={option.key} className="na-choice">
              <input
                type="radio"
                name={`${group}-penalty`}
                value={option.key}
                checked={penalty === option.key}
                onChange={() => setPenalty(option.key)}
                data-testid={`penalty-${option.key}`}
              />
              <span>{option.label}</span>
              <span className="small faint mono"> {option.rule}</span>
              {option.inquiry_mandatory ? (
                <span className="small warn-text">
                  {' '}
                  — an inquiry is mandatory (Rule 16(1-A))
                </span>
              ) : null}
            </label>
          ))}
        </fieldset>
      ) : null}

      <div className="na-form">
        <PaperField
          // The one answer that must come with a paper of its own is a
          // disagreement, and its paper is the authority's signed reasons.
          label={paperNeeded ? 'Attach its signed reasons' : 'Attach the signed paper'}
          file={file}
          onFile={setFile}
          testid={`attach-${offer.decision}`}
        />
        <DateField label="Decided on" value={on} onChange={setOn} />
        <button
          type="button"
          className="primary"
          disabled={Boolean(blocked) || !on || record.isPending}
          onClick={() => record.mutate()}
          data-testid={`record-${offer.decision}`}
        >
          {record.isPending ? 'Recording…' : 'Record the decision'}
        </button>
      </div>
      {blocked && choiceKey ? <p className="small muted">{blocked}</p> : null}
      <KindWarning message={warning} />
      <ErrorEnvelope error={record.error} />
    </div>
  )
}

/**
 * The allegations to charge, ticked — read off the complaint and the enquiry
 * report by the case record. A charge the reading did not find is named in a
 * line, behind its own button: the one text box on the card, because there is
 * no choice to offer for something nobody has read yet.
 */
function ChargesPicker({
  data,
  ticked,
  onTick,
  named,
  onNamed,
}: {
  data: CaseDetail
  ticked: string[]
  onTick: (id: string, on: boolean) => void
  named: string[]
  onNamed: (lines: string[]) => void
}) {
  const [adding, setAdding] = useState((data.allegations ?? []).length === 0)
  return (
    <fieldset className="na-charges" data-testid="charges">
      <legend>Tick the allegations the authority decided to charge</legend>
      {(data.allegations ?? []).length === 0 ? (
        <p className="small muted">
          No allegation has been read off the complaint or the enquiry report yet. Name each
          charge in a line.
        </p>
      ) : null}
      {(data.allegations ?? []).map((allegation) => (
        <label key={allegation.id} className="na-choice">
          <input
            type="checkbox"
            checked={ticked.includes(allegation.id)}
            onChange={(event) => onTick(allegation.id, event.target.checked)}
          />
          <span>{allegation.statement}</span>
          {allegation.filename ? (
            <span className="small faint mono">
              {' '}
              {allegation.filename}
              {allegation.page ? ` · p. ${allegation.page}` : ''}
            </span>
          ) : null}
        </label>
      ))}
      {adding ? (
        <div className="na-named">
          {[...named, ''].map((line, index) => (
            <input
              key={index}
              aria-label={`Charge not in the list, line ${index + 1}`}
              placeholder="A charge, in a line"
              value={line}
              onChange={(event) => {
                const next = [...named]
                next[index] = event.target.value
                onNamed(next.filter((item, i) => item.trim() || i < named.length))
              }}
            />
          ))}
        </div>
      ) : (
        <button type="button" className="quiet" onClick={() => setAdding(true)}>
          A charge not listed here
        </button>
      )}
    </fieldset>
  )
}

/* --------------------------------------------------------- service, reply -- */

function ServeOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const [on, setOn] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const serve = useMutation({
    mutationFn: async () => {
      if (file) {
        const done = await uploadPaper(data.id, file, offer.paper)
        setWarning(done.kindNotRecorded)
      }
      let receipt = null
      for (const document of offer.serves ?? []) {
        receipt = await api.recordService(data.id, {
          preflight: false,
          document_type: document,
          occurrence: 1,
          served_on: on,
        })
      }
      return receipt
    },
    onSuccess: () => {
      setFile(null)
      refresh()
    },
  })
  return (
    <div className="na-offer na-form" data-testid="offer-serve">
      <PaperField
        label={offer.paper_label ?? 'The acknowledgement of receipt'}
        file={file}
        onFile={setFile}
      />
      <DateField label="Served on" value={on} onChange={setOn} hint={READ_IT_OFF_THE_PAPER} />
      <button
        type="button"
        className="primary"
        disabled={!on || serve.isPending}
        onClick={() => serve.mutate()}
        data-testid="record-serve"
      >
        {serve.isPending ? 'Recording…' : offer.label}
      </button>
      <KindWarning message={warning} />
      <ErrorEnvelope error={serve.error} />
    </div>
  )
}

function ReplyOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const [on, setOn] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const reply = useMutation({
    mutationFn: async () => {
      if (file) {
        const done = await uploadPaper(data.id, file, offer.paper)
        setWarning(done.kindNotRecorded)
      }
      return api.recordReply(data.id, {
        preflight: false,
        to: offer.reply_to ?? 'chargesheet',
        received_on: on,
        mode: null,
        reference: null,
        read_down_note: null,
        note: null,
      })
    },
    onSuccess: () => {
      setFile(null)
      refresh()
    },
  })
  return (
    <div
      className={`na-offer na-form${offer.primary ? ' primary' : ''}`}
      data-testid="offer-reply"
    >
      <PaperField label={offer.paper_label ?? 'His reply'} file={file} onFile={setFile} />
      <DateField label="Received on" value={on} onChange={setOn} hint={READ_IT_OFF_THE_PAPER} />
      <button
        type="button"
        className={offer.primary ? 'primary' : undefined}
        disabled={!on || reply.isPending}
        onClick={() => reply.mutate()}
        data-testid="record-reply-arrived"
      >
        {reply.isPending ? 'Recording…' : offer.label}
      </button>
      <KindWarning message={warning} />
      <ErrorEnvelope error={reply.error} />
    </div>
  )
}

function ExtendOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const [open, setOpen] = useState(false)
  const [days, setDays] = useState<number | null>(null)
  const [on, setOn] = useState(data.as_of)
  const [file, setFile] = useState<File | null>(null)
  // One attempt, one key: the ledger appends, so a lost response and a second
  // click must not become a second grant (the extension command's docstring).
  const [attempt] = useState(() => crypto.randomUUID())
  const extend = useMutation({
    mutationFn: async () => {
      if (!offer.clock || !days) return
      if (file) await uploadPaper(data.id, file, offer.paper)
      await api.grantExtension(
        data.id,
        offer.clock,
        { preflight: false, days, reason: extensionReason(file?.name), granted_on: on },
        { idempotencyKey: `extend:${data.id}:${offer.clock}:${attempt}` },
      )
    },
    onSuccess: () => {
      setOpen(false)
      setFile(null)
      refresh()
    },
  })
  if (!open) {
    return (
      <div className="na-offer">
        <button type="button" onClick={() => setOpen(true)} data-testid="offer-extend">
          {offer.label}
        </button>
      </div>
    )
  }
  return (
    <div className="na-offer na-form" data-testid="offer-extend-form">
      <PaperField label={offer.paper_label ?? 'His letter'} file={file} onFile={setFile} />
      <label className="na-date">
        How many days
        <select
          aria-label="How many days"
          value={days ?? ''}
          onChange={(event) => setDays(event.target.value ? Number(event.target.value) : null)}
        >
          <option value="">— pick —</option>
          {(offer.days ?? []).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <DateField label="Granted on" value={on} onChange={setOn} />
      <button
        type="button"
        className="primary"
        disabled={!days || !on || extend.isPending}
        onClick={() => extend.mutate()}
      >
        {extend.isPending ? 'Granting…' : 'Record the further time'}
      </button>
      <button type="button" className="quiet" onClick={() => setOpen(false)}>
        Cancel
      </button>
      <ErrorEnvelope error={extend.error} />
    </div>
  )
}

/* --------------------------------------------------------------- answers -- */

function AnswersOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [on, setOn] = useState(data.as_of)
  const charges = (data.charges ?? []).map((c) => c.ordinal)
  const blocked = answersBlocked(charges, answers)
  const record = useMutation({
    mutationFn: () =>
      api.recordAnswers(data.id, {
        preflight: false,
        answers: charges.map((n) => ({
          charge: n,
          answer: answers[n] === 'admitted' ? 'admitted' : 'not_admitted',
        })),
        no_reply: false,
        decided_on: on,
        source_file_id: null,
        note: null,
      }),
    onSuccess: refresh,
  })
  return (
    <div className="na-offer na-answers" data-testid="offer-answers">
      <table className="na-answer-table">
        <tbody>
          {(data.charges ?? []).map((charge) => (
            <tr key={charge.ordinal}>
              <th scope="row">
                <span className="mono">{charge.ordinal}</span> {charge.heading}
              </th>
              {(['admitted', 'not_admitted'] as const).map((answer) => (
                <td key={answer}>
                  <label className="na-choice">
                    <input
                      type="radio"
                      name={`answer-${charge.ordinal}`}
                      checked={answers[charge.ordinal] === answer}
                      onChange={() =>
                        setAnswers((was) => ({ ...was, [charge.ordinal]: answer }))
                      }
                      data-testid={`answer-${charge.ordinal}-${answer}`}
                    />
                    <span>{answer === 'admitted' ? 'Admitted' : 'Not admitted'}</span>
                  </label>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="na-form">
        <DateField label="Read on" value={on} onChange={setOn} />
        <button
          type="button"
          className="primary"
          disabled={Boolean(blocked) || !on || record.isPending}
          onClick={() => record.mutate()}
          data-testid="record-answers"
        >
          {record.isPending ? 'Recording…' : offer.label}
        </button>
      </div>
      {blocked ? <p className="small muted">{blocked}</p> : null}
      <ErrorEnvelope error={record.error} />
    </div>
  )
}

function NoReplyOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const [sure, setSure] = useState(false)
  const record = useMutation({
    mutationFn: () =>
      api.recordAnswers(data.id, {
        preflight: false,
        answers: [],
        no_reply: true,
        decided_on: data.as_of,
        source_file_id: null,
        note: null,
      }),
    onSuccess: refresh,
  })
  if (!sure) {
    return (
      <div className="na-offer">
        <button type="button" onClick={() => setSure(true)} data-testid="offer-no-reply">
          {offer.label}
        </button>
      </div>
    )
  }
  return (
    <div className="na-offer na-form">
      <span className="small">No reply came, and the case proceeds without one.</span>
      <button
        type="button"
        className="primary"
        disabled={record.isPending}
        onClick={() => record.mutate()}
        data-testid="confirm-no-reply"
      >
        {record.isPending ? 'Recording…' : 'Record it'}
      </button>
      <button type="button" className="quiet" onClick={() => setSure(false)}>
        Cancel
      </button>
      <ErrorEnvelope error={record.error} />
    </div>
  )
}

/* ------------------------------------------------------------------ facts -- */

function FactOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  // Where the date IS the fact — the day his appeal was filed — it is read off
  // his paper and starts blank; where the fact is a state the offer names, the
  // date is only when the office recorded it, and today is the common case.
  const dateIsTheFact = !offer.fact_value
  const [on, setOn] = useState(dateIsTheFact ? '' : data.as_of)
  const [file, setFile] = useState<File | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const record = useMutation({
    mutationFn: async () => {
      if (!offer.fact) return
      if (file) {
        const done = await uploadPaper(data.id, file, offer.paper)
        setWarning(done.kindNotRecorded)
      }
      await api.recordFact(data.id, {
        preflight: false,
        path: offer.fact,
        // The date is the fact, unless the fact is a state the offer names.
        value: offer.fact_value ?? on,
        recorded_on: on,
        note: null,
      })
    },
    onSuccess: () => {
      setFile(null)
      refresh()
    },
  })
  return (
    <div className="na-offer na-form" data-testid={`offer-fact-${offer.fact}`}>
      <PaperField label={offer.paper_label ?? 'The paper'} file={file} onFile={setFile} />
      <DateField
        label="On"
        value={on}
        onChange={setOn}
        hint={dateIsTheFact ? READ_IT_OFF_THE_PAPER : undefined}
      />
      <button
        type="button"
        className={offer.primary ? 'primary' : undefined}
        disabled={!on || record.isPending}
        onClick={() => record.mutate()}
      >
        {record.isPending ? 'Recording…' : offer.label}
      </button>
      <KindWarning message={warning} />
      <ErrorEnvelope error={record.error} />
    </div>
  )
}

/* ------------------------------------------------------ the two officers -- */

interface Pick {
  personId: string
  name: string
  designation: string
}

const EMPTY_PICK: Pick = { personId: '', name: '', designation: '' }

function PersonPick({
  role,
  people,
  value,
  onChange,
}: {
  role: string
  people: RegisterPersonView[]
  value: Pick
  onChange: (pick: Pick) => void
}) {
  const outside = value.personId === 'outside'
  return (
    <div className="na-person">
      <label>
        {role}
        <select
          aria-label={role}
          value={value.personId}
          onChange={(event) => onChange({ ...EMPTY_PICK, personId: event.target.value })}
        >
          <option value="">— pick from the register —</option>
          {people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name_en ?? person.name_hi}
              {person.designation_en || person.designation_hi
                ? ` — ${person.designation_en ?? person.designation_hi}`
                : ''}
            </option>
          ))}
          <option value="outside">Not on the register</option>
        </select>
      </label>
      {outside ? (
        <div className="row">
          <input
            aria-label={`${role} — name`}
            placeholder="Name, as the order will print it"
            value={value.name}
            onChange={(event) => onChange({ ...value, name: event.target.value })}
          />
          <input
            aria-label={`${role} — designation`}
            placeholder="Designation"
            value={value.designation}
            onChange={(event) => onChange({ ...value, designation: event.target.value })}
          />
        </div>
      ) : null}
    </div>
  )
}

function pickReady(pick: Pick): boolean {
  return pick.personId === 'outside' ? Boolean(pick.name.trim()) : Boolean(pick.personId)
}

function asNominee(role: 'IO' | 'PO', pick: Pick) {
  return pick.personId === 'outside'
    ? {
        role,
        person_id: null,
        name: pick.name.trim(),
        designation: pick.designation.trim() || null,
      }
    : { role, person_id: pick.personId, name: null, designation: null }
}

function NominateOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  const people = useQuery({
    queryKey: ['case-people', data.id],
    queryFn: () => api.listCasePeople(data.id),
  })
  const [io, setIo] = useState<Pick>(EMPTY_PICK)
  const [po, setPo] = useState<Pick>(EMPTY_PICK)
  const [on, setOn] = useState(data.as_of)
  const record = useMutation({
    mutationFn: () =>
      api.recordDecision(data.id, {
        preflight: false,
        decision: offer.decision ?? 'officers-nominated',
        outcome: 'nominated',
        decided_on: on,
        note: null,
        source_file_id: null,
        charges: null,
        proposed_penalty: null,
        people: [asNominee('IO', io), asNominee('PO', po)],
      }),
    onSuccess: refresh,
  })
  const list = people.data ?? []
  return (
    <div className="na-offer na-nominate" data-testid="offer-nominate">
      <PersonPick role="Inquiry Officer" people={list} value={io} onChange={setIo} />
      <PersonPick role="Presenting Officer" people={list} value={po} onChange={setPo} />
      <div className="na-form">
        <DateField label="Proposed on" value={on} onChange={setOn} />
        <button
          type="button"
          className="primary"
          disabled={!pickReady(io) || !pickReady(po) || record.isPending}
          onClick={() => record.mutate()}
          data-testid="record-nomination"
        >
          {record.isPending ? 'Recording…' : 'Record the officers proposed'}
        </button>
      </div>
      <ErrorEnvelope error={people.error} />
      <ErrorEnvelope error={record.error} />
    </div>
  )
}

function AppointOffer({ data, offer }: { data: CaseDetail; offer: OfferView }) {
  const refresh = useRefreshCase(data.id)
  // Both blank: the receipt starts Rule 14(24)'s six months and is never moved
  // afterwards, and the order's date — asked only when the record does not
  // hold the order's issue — is the order's, not the receipt's.
  const [on, setOn] = useState('')
  const [orderOn, setOrderOn] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const appointedOn = offer.on ?? orderOn
  const record = useMutation({
    mutationFn: async () => {
      if (file) {
        const done = await uploadPaper(data.id, file, offer.paper)
        setWarning(done.kindNotRecorded)
      }
      // The order's own date is when they were appointed; the Inquiry
      // Officer's receipt is what starts his six months (Rule 14(24)).
      for (const person of offer.people ?? []) {
        const role = person.role === 'IO' ? 'IO' : person.role === 'PO' ? 'PO' : null
        if (!role) continue
        await api.recordAppointment(data.id, {
          preflight: false,
          role,
          appointed_on: appointedOn,
          person_id: person.person_id ?? null,
          person_name: person.person_id ? null : (person.name ?? null),
          designation: person.designation ?? null,
          received_on: role === 'IO' ? on : null,
          note: null,
        })
      }
    },
    onSuccess: () => {
      setFile(null)
      refresh()
    },
  })
  if ((offer.people ?? []).length === 0) {
    return (
      <div className="blocked unsettled">
        <div className="why">
          The officers proposed are not on the record, so there is nobody to record as
          appointed. Record the officers first — their order names them.
        </div>
      </div>
    )
  }
  return (
    <div className="na-offer na-form" data-testid="offer-appoint">
      <PaperField label={offer.paper_label ?? 'His receipt'} file={file} onFile={setFile} />
      {offer.on ? null : (
        <DateField
          label="The order is dated"
          value={orderOn}
          onChange={setOrderOn}
          hint="The record does not hold the order's issue — as written on the order."
        />
      )}
      <DateField
        label="He received it on"
        value={on}
        onChange={setOn}
        hint={READ_IT_OFF_THE_PAPER}
      />
      <button
        type="button"
        className="primary"
        disabled={!on || !appointedOn || record.isPending}
        onClick={() => record.mutate()}
        data-testid="record-appointment-receipt"
      >
        {record.isPending ? 'Recording…' : offer.label}
      </button>
      <span className="small muted">
        {(offer.people ?? [])
          .map(
            (p) =>
              `${p.role === 'IO' ? 'Inquiry Officer' : 'Presenting Officer'}: ${p.name ?? '—'}`,
          )
          .join(' · ')}
      </span>
      <KindWarning message={warning} />
      <ErrorEnvelope error={record.error} />
    </div>
  )
}
